import React from 'react';
import _ from 'lodash';
import { useDispatch } from 'react-redux';

import SiteStripe from '../functions/SiteStripe.js';
import SiteSales from '../functions/SiteSales.js';
import { actions } from '@/store/store.js';

function StripeForm({ cta, accountId, lineItems, stripeReturnUrl, subscription, scrollRef }) {
    const [loadingStripe, setLoadingStripe] = React.useState(true);
    const [stripeError, setStripeError] = React.useState(null);
    const [saleId, setSaleId] = React.useState(null);
    const [clientSecret, setClientSecret] = React.useState(null);
    const siteStripe = React.useRef(new SiteStripe()).current;
    const siteSales = new SiteSales();
    const emailRequired = !subscription && !accountId;
    const dispatch = useDispatch();
    const requiresShipping = lineItems?.some((item) => item.gps_shipping);

    React.useEffect(() => {
        if (subscription && !accountId) return;
        if (!lineItems || lineItems.length < 1) return;
        let salesProductIds = lineItems.map((item) => item.sales_product_id);
        salesProductIds = _.reject(salesProductIds, _.isNil);
        if (!salesProductIds.some((item => !!item))) return;

        if (subscription) {
            siteStripe.initializeSubscriptionSaleForm()
            .then(({ setupIntentId, clientSecret }) => {
                setClientSecret(clientSecret);
                return siteSales.createSubscriptionSale({ setupIntentId, salesProductIds });
            })
            .then((res) => res.json())
            .then(({ sale_id }) => setSaleId(sale_id))
            .then(() => setLoadingStripe(false));
        } else {
            siteStripe.initializeOneTimeSaleForm(lineItems, accountId)
            .then(({ paymentIntentId }) => siteSales.createOneTimeSale({ lineItems, accountId, paymentIntentId }))
            .then(({ sale_id }) => setSaleId(sale_id))
            .then(() => setLoadingStripe(false));
        }
    }, []);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoadingStripe(true);
        setStripeError(null);

        if (emailRequired) {
            const email = event.target.elements.email.value;
            if (!email) {
                setStripeError("An email address is required to ensure delivery.");
                return;
            }
            try {
                await dispatch(actions.sales.update({ id: saleId, email_address: email })).unwrap();
            } catch (e) {
                setStripeError(e.message);
                return;
            }
        }
        siteStripe.hideForm();
        requestAnimationFrame(() => {
            scrollRef?.current?.scrollTo({ top: 0, behavior: "smooth" });
        });

        let confirmation;
        if (subscription) {
            const returnUrl = new URL(stripeReturnUrl);
            returnUrl.searchParams.set("sale_id", saleId);
            returnUrl.searchParams.set("subscription", "true");
            confirmation = await siteStripe.confirmSubscription({
                returnUrl: returnUrl.toString(),
                clientSecret
            });
        } else {
            confirmation = await siteStripe.confirmPayment(stripeReturnUrl);
        }
        setLoadingStripe(false);

        if (confirmation.error) {
            siteStripe.showForm();
            if (confirmation.error.type === "card_error" || confirmation.error.type === "validation_error") {
                setStripeError(error.message);
            } else {
                setStripeError("An unexpected error occurred.");
            }
        }
    };

    return (
        <>
            {loadingStripe && (
                <div className="loading-indicator">Processing...</div>
            )}
            <div id="preload-error" className="hidden"></div>
            <div className="form stripe-form">
                <div className="api-message"></div>
                <div className="payment">
                    <div className="stripe-spinner" id="form-loading-spinner"></div>
                    <form id="payment-form" onSubmit={handleSubmit}>
                        <div id="payment-element" className="mb-3"></div>
                        {requiresShipping && (
                          <div className="address-element mb-3">
                            <b className="d-block mb-2">Shipping</b>
                            <div id="address-element" data-testid="address-element"></div>
                          </div>
                        )}
                        {emailRequired && (
                          <div className="form-floating">
                              <input
                                type="email"
                                className="form-control mb-3"
                                name="email"
                                id="emailInput"
                                placeholder="name@example.com"
                                required
                              />
                              <label htmlFor="emailInput">Email address</label>
                          </div>
                        )}
                        <button className="stripe-button btn site-btn w-100 py-4" disabled={loadingStripe}>
                            <span id="button-text">{cta}</span>
                        </button>
                        {stripeError && <div id="payment-message">{stripeError}</div>}
                    </form>
                </div>
            </div>
        </>
    );
}

export default StripeForm;